export interface AISUser {
  id: string;
  name: string;
  email?: string;
}

export interface FetchUsersByIdsOptions {
  path?: string;
  headers?: HeadersInit;
  signal?: AbortSignal;
}

export type UserDirectoryTransport = (
  ids: string[],
  options: FetchUsersByIdsOptions
) => Promise<unknown>;

export interface UserDirectoryOptions {
  fetchUsers?: UserDirectoryTransport;
  seedUsers?: readonly AISUser[];
  idField?: 'id' | 'uuid';
}

export class UserDirectoryError extends Error {
  constructor(readonly status: number) {
    super('User directory request failed');
    this.name = 'UserDirectoryError';
  }
}

interface RawDirectoryUser {
  id?: string | number | null;
  uuid?: string | null;
  name?: string | number | null;
  username?: string | number | null;
  email?: string | number | null;
  firstName?: string | number | null;
  lastName?: string | number | null;
}

function trimValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function extractUsers(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (Array.isArray(record.payload)) return record.payload;
    if (Array.isArray(record.users)) return record.users;
    if (Array.isArray(record.data)) return record.data;
  }
  return [];
}

export function formatAISUserName(
  user: Omit<RawDirectoryUser, 'id' | 'uuid'>,
  fallbackId: string
): string {
  const full = [trimValue(user.firstName), trimValue(user.lastName)].filter(Boolean).join(' ');
  return (
    full || trimValue(user.name) || trimValue(user.username) || trimValue(user.email) || fallbackId
  );
}

function normalize(raw: unknown, idField: 'id' | 'uuid'): AISUser | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const user = raw as RawDirectoryUser;
  const id = trimValue(user[idField]) || trimValue(user[idField === 'uuid' ? 'id' : 'uuid']);
  if (!id) return null;
  const email = trimValue(user.email);
  return { id, name: formatAISUserName(user, id), email: email || undefined };
}

async function fetchDirectoryUsers(
  ids: string[],
  options: FetchUsersByIdsOptions
): Promise<unknown> {
  const headers = new Headers(options.headers);
  headers.set('content-type', 'application/json');
  headers.set('accept', 'application/json');
  if (!headers.has('x-version')) headers.set('x-version', '2');
  const response = await fetch(options.path ?? '/api/resources/users/fetchUserList', {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ userList: ids }),
    signal: options.signal,
  });
  if (!response.ok) throw new UserDirectoryError(response.status);
  return response.json();
}

export function createUserDirectory({
  fetchUsers = fetchDirectoryUsers,
  seedUsers = [],
  idField = 'uuid',
}: UserDirectoryOptions = {}) {
  const userCache = new Map(seedUsers.map((user) => [user.id, user]));
  const unresolvedIds = new Set<string>();
  const inFlight = new Map<string, Promise<void>>();

  async function fetchBatch(ids: string[], options: FetchUsersByIdsOptions): Promise<void> {
    try {
      for (const raw of extractUsers(await fetchUsers(ids, options))) {
        const user = normalize(raw, idField);
        if (user) userCache.set(user.id, user);
      }
      for (const id of ids) {
        if (!userCache.has(id)) unresolvedIds.add(id);
      }
    } catch (error) {
      if (error instanceof UserDirectoryError && error.status === 401) throw error;
    }
  }

  function startBatch(ids: string[], options: FetchUsersByIdsOptions): Promise<void> {
    const pending = fetchBatch(ids, options).finally(() => {
      for (const id of ids) inFlight.delete(id);
    });
    for (const id of ids) inFlight.set(id, pending);
    return pending;
  }

  async function fetchUsersByIds(
    ids: string[],
    options: FetchUsersByIdsOptions = {}
  ): Promise<Map<string, AISUser>> {
    const uniqueIds = Array.from(new Set(ids.map(trimValue).filter(Boolean)));
    const pending = new Set<Promise<void>>();
    const toFetch: string[] = [];
    for (const id of uniqueIds) {
      if (userCache.has(id) || unresolvedIds.has(id)) continue;
      const lookup = inFlight.get(id);
      if (lookup) pending.add(lookup);
      else toFetch.push(id);
    }
    if (toFetch.length) pending.add(startBatch(toFetch, options));
    await Promise.all(pending);

    const result = new Map<string, AISUser>();
    for (const id of uniqueIds) {
      const user = userCache.get(id);
      if (user) result.set(id, user);
    }
    return result;
  }

  return { fetchUsersByIds };
}

export const fetchUsersByIds = createUserDirectory().fetchUsersByIds;
